import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../api/client';

export const AdminReferralsPage = () => {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPendingReferrals();
      setReferrals(response.data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load referrals');
      console.error('Error fetching referrals:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const toggleSelectAll = () => {
    if (selected.size === referrals.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(referrals.map(r => r.id)));
    }
  };

  const handleCredit = async () => {
    if (selected.size === 0) {
      setError('Please select referrals to credit');
      return;
    }

    if (!window.confirm(`Credit ${selected.size} referral earnings?`)) {
      return;
    }

    setProcessing(true);
    try {
      // Get unique referrer IDs from selected referrals
      const referrerEarnings = {};
      referrals.forEach(r => {
        if (selected.has(r.id)) {
          if (!referrerEarnings[r.referrer_id]) {
            referrerEarnings[r.referrer_id] = [];
          }
          referrerEarnings[r.referrer_id].push(r.id);
        }
      });

      // Credit each referrer's earnings
      for (const [referrerId, earningIds] of Object.entries(referrerEarnings)) {
        await adminAPI.creditReferralEarnings({
          referrerId: parseInt(referrerId),
          earningIds: earningIds,
        });
      }

      setSelected(new Set());
      fetchReferrals();
    } catch (err) {
      setError('Failed to credit referral earnings');
      console.error('Error crediting earnings:', err);
    } finally {
      setProcessing(false);
    }
  };

  const totalSelected = (() => {
    let total = 0;
    referrals.forEach(r => {
      if (selected.has(r.id)) {
        total += parseFloat(r.earning_amount);
      }
    });
    return total;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referral earnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-600">Referral Earnings</h1>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-gray-800"
          >
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-8 px-4">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600">
              Selected: <strong>{selected.size}</strong> | Total: <strong>₹{totalSelected.toFixed(2)}</strong>
            </p>
          </div>
          <button
            onClick={handleCredit}
            disabled={selected.size === 0 || processing}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? 'Processing...' : `Credit Selected (${selected.size})`}
          </button>
        </div>

        {/* Referrals Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {referrals.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No pending referral earnings
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="px-6 py-3">
                      <input
                        type="checkbox"
                        checked={selected.size === referrals.length && referrals.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-green-600"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Referrer</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Referred User</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Earning Amount</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral) => (
                    <tr key={referral.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selected.has(referral.id)}
                          onChange={() => toggleSelect(referral.id)}
                          className="w-4 h-4 text-green-600"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {referral.referrer_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {referral.referred_username}
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">
                        ₹{parseFloat(referral.earning_amount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                          Pending
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Card */}
        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">📊 Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <p className="text-sm text-yellow-800">Total Pending</p>
              <p className="text-2xl font-bold text-yellow-600">₹{referrals.reduce((sum, r) => sum + parseFloat(r.earning_amount), 0).toFixed(2)}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-blue-800">Number of Earnings</p>
              <p className="text-2xl font-bold text-blue-600">{referrals.length}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
              <p className="text-sm text-green-800">Selected Amount</p>
              <p className="text-2xl font-bold text-green-600">₹{totalSelected.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReferralsPage;
